/* WhAiBotChat (iter448) — AI Нутрибот / Ендобот chat panel.
 *
 * Calls POST /api/wh/ai-bot/chat with bot in {nutribot, endobot}.
 * Messages persist in component state only (history is logged server-side).
 */
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Salad, Brain, Send, BookOpen } from "lucide-react";
import { formatApiError } from "@/lib/formatApiError";

const BOTS = [
  {
    key: "auto",
    label: "AI Здравен Помощник",
    description: "Хранене + хормонален баланс за PCOS, ендометриоза, фертилитет, менопауза",
    icon: Brain,
    color: "from-pink-500 to-fuchsia-500",
    placeholder: "Например: Какво да закуся при PCOS? Защо имам нередовен цикъл?",
  },
];

export default function WhAiBotChat({ API, isDark, defaultBot = "auto", context }) {
  // iter452: ensure defaultBot exists in BOTS array, fallback to "auto"
  const initialBot = BOTS.find((b) => b.key === defaultBot) ? defaultBot : "auto";
  const [bot, setBot] = useState(initialBot);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const botCfg = BOTS.find((b) => b.key === bot) || BOTS[0];

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const msg = input.trim();
    if (!msg || sending) return;
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/wh/ai-bot/chat`, {
        bot, message: msg, context: context || null,
      });
      setMessages((m) => [...m, { role: "bot", text: data.response, disclaimer: data.disclaimer_bg }]);
    } catch (e) {
      const detail = formatApiError(e, "Грешка при свързване с AI");
      toast.error(detail);
      setMessages((m) => [...m, { role: "bot", text: `Грешка: ${detail}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const Icon = botCfg.icon;

  return (
    <Card className={isDark ? "bg-slate-950 border-slate-800" : "bg-white border-[#E5E2DA]"} data-testid="wh-ai-bot-chat">
      <CardContent className="p-4 sm:p-5">
        {/* Bot selector — hidden when only one bot (auto-mode) */}
        {BOTS.length > 1 && (
          <div className="flex gap-2 mb-3">
            {BOTS.map((b) => {
            const BIcon = b.icon;
            const active = bot === b.key;
            return (
              <button
                key={b.key}
                type="button"
                onClick={() => setBot(b.key)}
                className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-bold transition ${
                  active
                    ? `bg-gradient-to-br ${b.color} text-white border-transparent shadow`
                    : isDark
                      ? "bg-slate-900 border-slate-800 text-slate-300"
                      : "bg-white border-[#E5E2DA] text-[#5C665D]"
                }`}
                data-testid={`wh-ai-bot-tab-${b.key}`}
              >
                <BIcon className="w-3.5 h-3.5" />
                {b.label}
              </button>
            );
          })}
          </div>
        )}

        {/* Description */}
        <div className={`flex items-center gap-2 mb-3 text-[11px] ${isDark ? "text-slate-400" : "text-[#5C665D]"}`}>
          <BookOpen className="w-3.5 h-3.5" />
          <span>{botCfg.description} · Цитира PubMed</span>
        </div>

        {/* Messages */}
        <div
          ref={scrollRef}
          className={`min-h-[280px] max-h-[480px] overflow-y-auto rounded-xl border p-3 mb-3 space-y-3 ${
            isDark ? "bg-slate-900/60 border-slate-800" : "bg-[#FBF8F1] border-[#E5E2DA]"
          }`}
          data-testid="wh-ai-bot-messages"
        >
          {messages.length === 0 && (
            <div className={`flex flex-col items-center justify-center py-10 text-center ${isDark ? "text-slate-500" : "text-[#8F9990]"}`}>
              <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${botCfg.color} flex items-center justify-center text-white mb-2`}>
                <Icon className="w-6 h-6" />
              </div>
              <p className="text-sm font-bold mb-1">Здравейте, аз съм {botCfg.label}</p>
              <p className="text-[11px] max-w-xs">
                Задайте въпрос за храненето или хормоните си. Образователен отговор, не медицинска консултация.
              </p>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`wh-ai-bot-msg-${i}`}>
              <div
                className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${
                  m.role === "user"
                    ? isDark ? "bg-pink-600 text-white" : "bg-pink-500 text-white"
                    : m.error
                      ? isDark ? "bg-rose-950/40 text-rose-200 border border-rose-800" : "bg-rose-50 text-rose-900 border border-rose-200"
                      : isDark ? "bg-slate-800 text-slate-100" : "bg-white text-[#2A342B] border border-[#E5E2DA]"
                }`}
              >
                {m.text}
                {m.disclaimer && (
                  <p className={`text-[10px] mt-1.5 italic opacity-70`}>{m.disclaimer}</p>
                )}
              </div>
            </div>
          ))}
          {sending && (
            <div className="flex justify-start" data-testid="wh-ai-bot-loading">
              <div className={`rounded-2xl px-3 py-2 text-sm ${isDark ? "bg-slate-800 text-slate-300" : "bg-white text-[#5C665D] border border-[#E5E2DA]"}`}>
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Търси в научните източници…
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={botCfg.placeholder}
            rows={2}
            disabled={sending}
            className={`flex-1 px-3 py-2 rounded-xl border text-sm resize-none ${
              isDark ? "bg-slate-900 border-slate-700 text-slate-100" : "bg-white border-[#E5E2DA] text-[#2A342B]"
            }`}
            data-testid="wh-ai-bot-input"
          />
          <Button
            onClick={send}
            disabled={sending || !input.trim()}
            className={`bg-gradient-to-br ${botCfg.color} text-white`}
            data-testid="wh-ai-bot-send"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
